import { DownloadSimpleIcon, FileIcon } from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import type { Track, TrackFile, TrackPoints } from '@/lib/db';
import { decodeTrackFile, trackFiles } from '@/lib/db';
import { formatBytes } from '@/lib/format';
import { exportTrack, saveFile, type TrackExportFormat } from '@/lib/track-export';

/**
 * The files behind a flight: the originals it was imported from, each
 * downloadable byte for byte, and the stored track written out as GPX or CSV.
 *
 * The export buttons show for every track, not only the ones with no
 * original. An FR24 KML is the right thing to archive and the wrong thing to
 * hand an EFB that wants a GPX, and converting it is exactly what a pilot
 * would otherwise go and find another tool to do.
 */

export interface TrackFilesPanelProps {
  track: Track;
  points: TrackPoints;
}

function downloadOriginal(file: TrackFile): void {
  saveFile(file.filename, decodeTrackFile(file).slice().buffer, file.mediaType);
}

function downloadExport(track: Track, points: TrackPoints, format: TrackExportFormat): void {
  const { filename, mediaType, text } = exportTrack(track, points, format);
  saveFile(filename, text, mediaType);
}

export function TrackFilesPanel({ track, points }: TrackFilesPanelProps) {
  const files = useLiveQuery(() => trackFiles.listByTrack(track.id), [track.id], null);

  return (
    <section
      className="border-border bg-surface rounded-container space-y-3 border p-3"
      aria-labelledby="track-files-heading"
      data-testid="track-files-panel"
    >
      <h2
        id="track-files-heading"
        className="text-muted-foreground text-[0.625rem] font-medium tracking-wider uppercase"
      >
        Files
      </h2>

      {files === null ? null : files.length === 0 ? (
        <p className="text-muted-foreground text-sm">No original file stored.</p>
      ) : (
        <ul className="space-y-1.5">
          {files.map((file) => (
            <li key={file.id} className="flex items-center gap-2 text-sm">
              <FileIcon
                aria-hidden="true"
                weight={ICON_WEIGHT}
                className="text-muted-foreground size-4 shrink-0"
              />
              <span className="min-w-0 flex-1 truncate" title={file.filename}>
                {file.filename}
              </span>
              <span className="text-muted-foreground tabular shrink-0 text-xs">
                {formatBytes(file.sizeBytes)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Download ${file.filename}`}
                onClick={() => {
                  downloadOriginal(file);
                }}
              >
                <DownloadSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            downloadExport(track, points, 'gpx');
          }}
        >
          <DownloadSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          Export GPX
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            downloadExport(track, points, 'csv');
          }}
        >
          <DownloadSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          Export CSV
        </Button>
      </div>
    </section>
  );
}
